/**
 * Group the recap's `lines` (end user x product line x ship-to x SKU) into the
 * workbook's pivot shapes: End user, End user > Ship-to > SKU, End user >
 * Product line > Ship-to, SKU > Ship-to. One dataset, every shape, so the
 * Detail view cannot disagree with the Overview: a group's figures are the sums
 * of the lines under it, and the variances are worked out from those sums --
 * never summed themselves.
 */

import type { Keyed } from '../shared/rows'
import { text } from '../shared/rows'

export type Level = 'end_user' | 'product_line' | 'ship_to' | 'sku'

export interface Figures {
  readonly cy: number
  readonly py: number
  readonly plan: number
}

export type TreeNode = GroupNode<Figures>

export const LAYOUTS: readonly {
  key: string
  label: string
  levels: readonly Level[]
}[] = [
  { key: 'eu', label: 'End user', levels: ['end_user'] },
  {
    key: 'eu-st-sku',
    label: 'End user › Ship-to › SKU',
    levels: ['end_user', 'ship_to', 'sku'],
  },
  {
    key: 'eu-pl-st',
    label: 'End user › Product line › Ship-to',
    levels: ['end_user', 'product_line', 'ship_to'],
  },
  { key: 'sku-st', label: 'SKU › Ship-to', levels: ['sku', 'ship_to'] },
  {
    key: 'pl-eu-st',
    label: 'Product line › End user › Ship-to',
    levels: ['product_line', 'end_user', 'ship_to'],
  },
]

function value(row: Keyed, key: string): number {
  const raw = row[key]
  return typeof raw === 'number' ? raw : Number(raw ?? 0)
}

export function figuresOf(row: Keyed): Figures {
  return {
    cy: value(row, 'cy'),
    py: value(row, 'py'),
    plan: value(row, 'plan'),
  }
}

export function sum(items: readonly Figures[]): Figures {
  return items.reduce(
    (total, item) => ({
      cy: total.cy + item.cy,
      py: total.py + item.py,
      plan: total.plan + item.plan,
    }),
    { cy: 0, py: 0, plan: 0 },
  )
}

/** The value a row groups on at `level`, and how it reads. */
export function keyOf(row: Keyed, level: Level): { key: string; label: string } {
  switch (level) {
    case 'end_user': {
      const name = text(row['end_user']) ?? '(no end user)'
      return { key: name, label: name }
    }
    case 'product_line': {
      const name = text(row['product_line']) ?? '(no product line)'
      return { key: name, label: name }
    }
    case 'ship_to': {
      const id = text(row['ship_to']) ?? ''
      const name = text(row['ship_to_customer'])
      return { key: id, label: name === null ? id : `${name} (${id})` }
    }
    case 'sku': {
      const sku = text(row['sku']) ?? ''
      return { key: sku, label: text(row['item']) ?? sku }
    }
  }
}

/** A node of any figure type: the Detail tree carries totals, the month tree
 *  twelve months of each. */
export interface GroupNode<F> {
  readonly id: string
  readonly label: string
  readonly level: Level
  readonly depth: number
  readonly figures: F
  readonly children: readonly GroupNode<F>[]
}

/** Groups at each level, each node's figures the sum of the rows under it,
 *  largest first by `size` (then by name). */
export function groupTree<F>(
  lines: readonly Keyed[],
  levels: readonly Level[],
  measure: (row: Keyed) => F,
  add: (items: readonly F[]) => F,
  size: (figures: F) => number,
  depth = 0,
  parent = '',
): GroupNode<F>[] {
  const level = levels[depth]
  if (level === undefined) return []
  const groups = new Map<string, { label: string; rows: Keyed[] }>()
  for (const row of lines) {
    const { key, label } = keyOf(row, level)
    const group = groups.get(key)
    if (group === undefined) groups.set(key, { label, rows: [row] })
    else group.rows.push(row)
  }
  const nodes: GroupNode<F>[] = []
  for (const [key, group] of groups) {
    const id = `${parent}/${level}:${key}`
    nodes.push({
      id,
      label: group.label,
      level,
      depth,
      figures: add(group.rows.map(measure)),
      children: groupTree(group.rows, levels, measure, add, size, depth + 1, id),
    })
  }
  return nodes.sort((a, b) => size(b.figures) - size(a.figures) || a.label.localeCompare(b.label))
}

/** Groups at each level, largest this year first (then by name). */
export function buildTree(lines: readonly Keyed[], levels: readonly Level[]): TreeNode[] {
  return groupTree(lines, levels, figuresOf, sum, (figures) => figures.cy)
}

// --- month by month ------------------------------------------------------------------

export const MONTHS = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep']

/** Twelve fiscal months (index 0 = October) of each of the three numbers. */
export interface MonthFigures {
  readonly cy: readonly number[]
  readonly py: readonly number[]
  readonly plan: readonly number[]
}

export function monthFiguresOf(row: Keyed): MonthFigures {
  const series = (prefix: string) =>
    MONTHS.map((_name, index) => value(row, `${prefix}_${String(index + 1).padStart(2, '0')}`))
  return { cy: series('cy'), py: series('py'), plan: series('plan') }
}

export function sumMonths(items: readonly MonthFigures[]): MonthFigures {
  const add = (key: keyof MonthFigures) =>
    MONTHS.map((_name, index) => items.reduce((total, item) => total + (item[key][index] ?? 0), 0))
  return { cy: add('cy'), py: add('py'), plan: add('plan') }
}

export function buildMonthTree(lines: readonly Keyed[], levels: readonly Level[]): GroupNode<MonthFigures>[] {
  return groupTree(lines, levels, monthFiguresOf, sumMonths, (figures) =>
    figures.cy.reduce((total, month) => total + month, 0),
  )
}

/** The rows on screen: every node whose ancestors are all open. */
export function visible<F>(nodes: readonly GroupNode<F>[], open: ReadonlySet<string>): GroupNode<F>[] {
  const out: GroupNode<F>[] = []
  const walk = (list: readonly GroupNode<F>[]) => {
    for (const node of list) {
      out.push(node)
      if (open.has(node.id)) walk(node.children)
    }
  }
  walk(nodes)
  return out
}

export function variance(cy: number, base: number): { amount: number; pct: number | null } {
  return {
    amount: cy - base,
    pct: base === 0 ? null : ((cy - base) / Math.abs(base)) * 100,
  }
}
